import { useMemo, useState } from "react";
import { PAGE_POLICIES, type PagePolicy } from "@medicorepay/shared";

const COVERAGE_FILTERS = [
  { id: "all", label: "All" },
  { id: "missing", label: "Missing module" },
  { id: "unassigned", label: "Unassigned" },
  { id: "disabled", label: "Not released" },
] as const;

type CoverageFilter = (typeof COVERAGE_FILTERS)[number]["id"];

interface RouteCoverageTableProps {
  moduleMap: Map<string, unknown>;
}

function exportPagePolicies(policies: readonly PagePolicy[]) {
  const headers = [
    "Key",
    "Section",
    "Page",
    "Route",
    "Module",
    "Permission",
    "Release",
  ];
  const rows = policies.map((policy) => ({
    Key: policy.key,
    Section: policy.section,
    Page: policy.label,
    Route: policy.path,
    Module: policy.requiredModule ?? "",
    Permission: policy.requiredPermission ?? "",
    Release: policy.release,
  }));
  const csvLines = [headers.join(",")];
  rows.forEach((row) => {
    const values = headers.map((key) => {
      const value = String((row as Record<string, string>)[key] ?? "");
      return `"${value.replace(/"/g, '""')}"`;
    });
    csvLines.push(values.join(","));
  });
  const blob = new Blob([csvLines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "page-policy-coverage.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export function RouteCoverageTable({ moduleMap }: RouteCoverageTableProps) {
  const [search, setSearch] = useState("");
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>("all");

  const missingCount = PAGE_POLICIES.filter(
    (policy) => policy.requiredModule && !moduleMap.has(policy.requiredModule),
  ).length;
  const unassignedCount = PAGE_POLICIES.filter(
    (policy) => policy.navigation && !policy.requiredModule,
  ).length;

  const coverageRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return PAGE_POLICIES.filter((policy) => {
      const isMissing = Boolean(
        policy.requiredModule && !moduleMap.has(policy.requiredModule),
      );
      const isUnassigned = !policy.requiredModule;
      const isDisabled = policy.release === "DISABLED";
      if (coverageFilter === "missing" && !isMissing) return false;
      if (coverageFilter === "unassigned" && !isUnassigned) return false;
      if (coverageFilter === "disabled" && !isDisabled) return false;
      if (!term) return true;
      return [
        policy.key,
        policy.label,
        policy.path,
        policy.section,
        policy.requiredModule,
        policy.requiredPermission,
      ].some((value) => value?.toLowerCase().includes(term));
    });
  }, [coverageFilter, moduleMap, search]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Page-policy coverage
            </h3>
            <p className="text-sm text-slate-500">
              {PAGE_POLICIES.length} code-owned policies · {missingCount}{" "}
              missing modules · {unassignedCount} unassigned navigation pages
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search policies, routes, modules"
              className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
            />
            <button
              type="button"
              onClick={() => exportPagePolicies(coverageRows)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600"
            >
              Export
            </button>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {COVERAGE_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setCoverageFilter(filter.id)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                    coverageFilter === filter.id
                      ? "bg-slate-900 text-white"
                      : "text-slate-600"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-700">
          Read-only runtime authority. Change page metadata in the shared
          page-policy manifest and deploy it with the application. Tenant
          exceptions belong in Page &amp; Sidebar Controls.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Section</th>
              <th className="px-4 py-3">Page / key</th>
              <th className="px-4 py-3">Route</th>
              <th className="px-4 py-3">Module</th>
              <th className="px-4 py-3">Permission</th>
              <th className="px-4 py-3">Release</th>
            </tr>
          </thead>
          <tbody>
            {coverageRows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  No page policies match the current filter.
                </td>
              </tr>
            ) : (
              coverageRows.map((policy) => {
                const moduleMissing = Boolean(
                  policy.requiredModule &&
                  !moduleMap.has(policy.requiredModule),
                );
                return (
                  <tr
                    key={policy.key}
                    className="border-b border-slate-100 last:border-none"
                  >
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {policy.section}
                    </td>
                    <td className="px-4 py-3">
                      <span className="block font-semibold text-slate-900">
                        {policy.label}
                      </span>
                      <span className="block font-mono text-[10px] text-slate-400">
                        {policy.key}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">
                      {policy.path}
                    </td>
                    <td
                      className={`px-4 py-3 font-mono text-xs ${
                        moduleMissing ? "text-rose-600" : "text-slate-600"
                      }`}
                    >
                      {policy.requiredModule ?? "—"}
                      {moduleMissing && " (missing)"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">
                      {policy.requiredPermission ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                          policy.release === "ENABLED"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {policy.release}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
